<template>
  <div v-if="showLoader" class="progressive-loader-overlay">
    <div class="progressive-loader-content">
      <div class="loader-header">
        <h5>{{ loaderTitle }}</h5>
        <p class="text-muted">
          <template v-if="isCombining">
            Combining all months into the final file...
          </template>
          <template v-else>
            Loading month {{ currentMonth }}/{{ currentYear }}
            ({{ currentProgress }} of {{ totalMonths }} blocks)
          </template>
        </p>
      </div>
      
      <div class="progress-container">
        <div class="progress-bar">
          <div 
            class="progress-fill" 
            :style="{ width: percentage + '%' }"
          ></div>
        </div>
        <div class="progress-text">
          {{ percentage }}% completed
        </div>
      </div>
      
      <div class="loader-details">
        <div class="detail-item">
          <span class="detail-label">Blocks processed:</span>
          <span class="detail-value">{{ currentProgress }}/{{ totalMonths }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Current month:</span>
          <span class="detail-value">{{ getMonthName(currentMonth) }} {{ currentYear }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Estimated time:</span>
          <span class="detail-value">{{ estimatedTime }}</span>
        </div>
      </div>
      
      <div class="loader-actions">
        <b-button 
          variant="outline-secondary" 
          size="sm"
          @click="$emit('cancel')"
          :disabled="!allowCancel"
        >
          Cancel
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue'

export default {
  name: 'ProgressiveLoader',
  components: {
    BButton
  },
  props: {
    showLoader: {
      type: Boolean,
      default: false
    },
    currentProgress: {
      type: Number,
      default: 0
    },
    totalMonths: {
      type: Number,
      default: 0
    },
    currentMonth: {
      type: Number,
      default: 0
    },
    currentYear: {
      type: Number,
      default: 0
    },
    allowCancel: {
      type: Boolean,
      default: true
    },
    loaderTitle: {
      type: String,
      default: 'Loading Data Progressively',
    },
    combining: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isCombining() {
      return this.combining
    },
    percentage() {
      if (this.totalMonths === 0) return 0
      return Math.round((this.currentProgress / this.totalMonths) * 100)
    },
    estimatedTime() {
      if (this.currentProgress === 0) return 'Calculating...'
      
      const remainingMonths = this.totalMonths - this.currentProgress
      const estimatedSeconds = remainingMonths * 2 // Estimate 2 seconds per month
      
      if (estimatedSeconds < 60) {
        return `${estimatedSeconds} seconds`
      } else {
        const minutes = Math.ceil(estimatedSeconds / 60)
        return `${minutes} minute${minutes > 1 ? 's' : ''}`
      }
    }
  },
  methods: {
    getMonthName(month) {
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ]
      return months[month - 1] || ''
    }
  }
}
</script>

<style lang="scss" scoped>
.progressive-loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

.progressive-loader-content {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  text-align: center;
  max-height: 90vh;
  overflow-y: auto;
}

.loader-header {
  margin-bottom: 1.5rem;
  
  h5 {
    margin-bottom: 0.5rem;
    color: #333;
    font-weight: 600;
  }
  
  p {
    margin-bottom: 0;
    font-size: 0.9rem;
  }
}

.progress-container {
  margin-bottom: 1.5rem;
}

.progress-bar {
  width: 100%;
  height: 12px;
  background: #e9ecef;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #007bff, #0056b3);
  border-radius: 6px;
  transition: width 0.3s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    animation: shimmer 2s infinite;
  }
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.progress-text {
  font-size: 0.9rem;
  font-weight: 600;
  color: #007bff;
}

.loader-details {
  margin-bottom: 1.5rem;
  text-align: left;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
}

.detail-label {
  font-size: 0.9rem;
  color: #666;
}

.detail-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
}

.loader-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

// Responsive improvements
@media (max-width: 576px) {
  .progressive-loader-overlay {
    padding: 0.5rem;
  }
  
  .progressive-loader-content {
    padding: 1.5rem;
    margin: 0;
    width: 95%;
    max-height: 95vh;
  }
  
  .loader-header h5 {
    font-size: 1.1rem;
  }
  
  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
  
  .progress-container {
    margin-bottom: 1rem;
  }
  
  .loader-details {
    margin-bottom: 1rem;
  }
}

@media (max-width: 480px) {
  .progressive-loader-content {
    padding: 1rem;
  }
  
  .loader-header h5 {
    font-size: 1rem;
  }
  
  .progress-text {
    font-size: 0.8rem;
  }
  
  .detail-label,
  .detail-value {
    font-size: 0.8rem;
  }
}

// Landscape orientation on mobile
@media (max-width: 767px) and (orientation: landscape) {
  .progressive-loader-content {
    max-height: 80vh;
    padding: 1.5rem;
  }
  
  .loader-header {
    margin-bottom: 1rem;
  }
  
  .progress-container {
    margin-bottom: 1rem;
  }
  
  .loader-details {
    margin-bottom: 1rem;
  }
}
</style> 